import Icon24, { Icon20 } from 'builder/components/Icon'
import Button, { ButtonTheme } from 'builder/components/Button'
import { ApplyButtonIcon } from 'builder/components/FindJob/JobDetails/styles'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import PropTypes from 'prop-types'
import { useI18n } from 'builder/hooks/useI18n'
import { actions as renderingActions } from 'builder/modules/rendering'
import { useFields } from 'builder/hooks/useFields'
import { ColumnName, actions } from 'builder/modules/jobTracking'
import Field from './Field'
import EditIcon from './Icons/Edit.svg'
import { StatusSelect } from '../CardStatusDrawer/StatusSelect'
import { groupBy, get } from 'lodash'
import { Status } from './Status'
import { HeaderClose, DrawerActionContainer } from '../CardDrawer/styles'
import {
  Container,
  PaddedContainer,
  Header,
  CompanyLogoContainer,
  CompanyNameContainer,
  CompanyLogo,
  CompanyLabel,
  CompanyName,
  ButtonContainer,
  PrimaryButton,
  InformationContainer,
  Title,
  Information,
  InformationDiv,
  ResumeMatch,
  ResumePercentage,
  ResumeMatchText,
  Actions,
  IrrelevantBox,
  IrrelevantBoxSubtitle,
  IrrelevantBoxTitle,
  IrrelevantButton,
  IrrelevantDeleteButton,
  TickIcon,
  TextAreaContainer,
  FieldLabel,
  ScrollContainer,
  ExternalLink,
  Chevron,
  HeaderAction,
  IconButton,
  DeleteIcon,
  CloseIcon,
  StatusesContainer,
  StatusInfoContainer,
  HeaderContainer,
  DeleteButtonContainer,
  DeleteButton,
  TailoredResumeReadyBanner,
  TailoredResumeContainer,
  TailoredResumeInformation,
  TailoredResumeData,
  TailoredResumeName,
  TailoredResumeInteractions,
} from './styles'
import { useLocation } from 'react-router-dom'
import JobSearchInfo from './JobSearchInfo'
import { apiClient } from 'builder/modules/apiClient'
import { AutoApplyStatusLabel } from '../AutoApplyStatusLabel'
import { useAutoApply } from 'builder/views/AutoApply/hooks/useAutoApply'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useScrollToAnchor } from 'builder/hooks/useScrollToAnchor'
import { stripHtml } from 'builder/utils/stripHTML'
import Tooltip from 'builder/components/Tooltip'
import { navigate } from 'builder/modules/navigate'
import { UploadedDocuments } from './UploadedDocuments/UploadedDocuments'
import { AutoApplyJobStatusInfo } from './AutoApplyJobStatusInfo/AutoApplyJobStatusInfo'
import ResumeTailoring from 'builder/features/dashboard/components/ProgressTracker/icons/ResumeTailoring'
import SelfTailorResumeModal from 'builder/components/SelfTailorResumeModal'

export const documentSectionId = 'auto-apply-documents'

const Sidebar = ({
  job,
  onSubmit,
  onClose,
  onDelete,
  handleSetInterviewJob,
  isDashboard,
  columnNames,
  stateChangeRef,
  showResumeWasTailoredBanner,
}) => {
  const dispatch = useDispatch()
  const routeLocation = useLocation()

  const { isPhone } = useMediaQueries()
  const { i18n } = useI18n()
  const ref = useRef(null)
  const columnNamesArray = columnNames || []
  const jobObject = job || {}
  const {
    id,
    company,
    title,
    salary,
    link,
    location,
    description,
    notes,
    status,
    logo,
    resumeScore,
    isIrrelevant,
    stillRelevant,
    externalSlugId,
    positionNumber,
    resumeId,
  } = jobObject

  const [fields, handleUpdate] = useFields({
    company,
    logo,
    title,
    salary,
    link,
    location,
    description,
    notes,
    status,
    isIrrelevant,
    stillRelevant,
  })

  const {
    isUserOnAutoApplyPlan,
    redirectToBillingPlans,
    redirectToAutoApplyQuestionnaire,
    isAutoApplyLimitReached,
  } = useAutoApply()
  useScrollToAnchor()

  const [companyState, setCompanyState] = useState(company)
  const [titleState, setTitleState] = useState(title)
  const [statusListOpen, setStatusListOpen] = useState(false)
  const [locationState, setLocationState] = useState(location)
  const [linkState, setLinkState] = useState(link)
  const [salaryState, setSalaryState] = useState(salary)
  const [notesState, setNotesState] = useState(notes)
  const [descriptionState, setDescriptionState] = useState(description)
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false)
  const [isNotesVisible, setIsNotesVisible] = useState(false)
  const [isDocumentsVisible, setIsDocumentsVisible] = useState(false)
  const [relevant, setRelevant] = useState(false)
  const [uploadingFile, setUplodingFile] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  const showApply = externalSlugId && [ColumnName.wishlist, ColumnName.recommended].includes(status)
  const poorResumeScore = !!(resumeScore && resumeScore < 70)

  const handleResumeLink = () => {
    trackInternalEvent('click_resume_link')
    const editorUrl = `/resumes/${resumeId}/edit`
    navigate(editorUrl)
  }

  const handleBlur = () => {
    if (relevant) {
      trackInternalEvent('mark_job_relevant')
      onSubmit({ isIrrelevant: false, stillRelevant: true, id })
    } else {
      onSubmit({ ...fields, id })
    }
  }

  const handleApplyClick = () => {
    window.open(fields.link, '_blank')

    const payload = {
      source: {
        droppableId: status,
        index: positionNumber,
      },
      destination: {
        droppableId: 'applied',
        index: 0,
      },
      jobId: Number(id),
    }

    dispatch(actions.setApplyTrackingDialog({ show: true, moveData: payload }))
  }

  const handleAutoApplyClick = () => {
    if (redirectToBillingPlans()) return
    if (redirectToAutoApplyQuestionnaire()) return
    if (isAutoApplyLimitReached()) return

    const payload = {
      source: {
        droppableId: status,
        index: positionNumber,
      },
      destination: {
        droppableId: ColumnName.auto_apply,
        index: 0,
      },
      jobId: Number(id),
    }
    dispatch(actions.moveRequest(payload))
    trackInternalEvent('auto_apply_for_job', {
      label: 'job_tracker',
      parameter: 'sidebar',
    })
  }

  const handleUpdateNote = () => {
    trackInternalEvent('update_note')
    handleBlur()
  }

  const openSectionsByStatus = useCallback(() => {
    if (
      ['auto_applied', 'error', 'no_application_made', 'information_required'].includes(
        job?.autoApplyStatus,
      )
    ) {
      setIsNotesVisible(true)
      setIsDocumentsVisible(true)
    }
  }, [job?.autoApplyStatus])

  const openDocumentSection = useCallback(() => {
    if (routeLocation.hash === `#${documentSectionId}`) {
      setIsDocumentsVisible(true)
    }
  }, [routeLocation.hash])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        (!stateChangeRef?.current || !stateChangeRef.current.contains(event.target))
      ) {
        onClose && onClose()
      }
    }
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [onClose, stateChangeRef])

  useEffect(() => {
    setTitleState(job.title)
    setLocationState(job.location)
    setNotesState(job.notes)
    setDescriptionState(job.description ? stripHtml(job.description) : job.description)
    setRelevant(job.isIrrelevant)
  }, [job])

  useEffect(() => {
    job.isIrrelevant && trackInternalEvent('view_irrelevantjob_notification')
  }, [job.isIrrelevant])

  useEffect(() => {
    openDocumentSection()
    openSectionsByStatus()
  }, [openDocumentSection, openSectionsByStatus, routeLocation])

  const autoApplyDocuments = groupBy(job.autoApplyDocuments, ({ documentType }) => documentType)

  const fileInputRefResume = useRef(null)
  const fileInputRefCoverLetter = useRef(null)
  const handleUploadDocumentButton = (event, documentType) => {
    event.preventDefault()
    event.stopPropagation()
    if (documentType === 'resume') {
      fileInputRefResume.current.click()
    } else {
      fileInputRefCoverLetter.current.click()
    }
  }

  const resetFileInputSelection = () => {
    fileInputRefResume.current.value = ''
    fileInputRefCoverLetter.current.value = ''
  }

  function handleChange(event, documentType) {
    handleSubmit(event.target.files[0], documentType)
  }

  function handleSubmit(file, documentType) {
    trackInternalEvent('document_upload', { label: 'job_tracker', parameter: documentType })

    const formData = new FormData()
    formData.append('auto_apply_document[document_type]', documentType)
    formData.append('auto_apply_document[document_file]', file)

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }

    setUplodingFile({ file, documentType })

    apiClient
      .post(`/job-cards/${job.id}/auto-apply-documents`, formData, config)
      .then(response => {
        dispatch(
          actions.updateJob({
            ...job,
            autoApplyDocuments: [...(job?.autoApplyDocuments || []), response.data],
          }),
        )
        setUplodingFile(null)
      })
      .catch(error => {
        console.error('Error uploading file: ', error)
        setUplodingFile(null)
        const errMsg = get(error, ['response', 'data', 'documentFile', '0'])
        if (errMsg) {
          setUploadError({ error: errMsg, documentType, delete: false })
        }
      })
  }

  function handleDownloadDocument() {
    dispatch(
      renderingActions.download({
        id: job.resumeId,
      }),
    )
  }

  function handleDeleteDocument(documentId, docType) {
    // optimistic update
    trackInternalEvent('document_removal', { label: 'job_tracker', parameter: docType })
    const autoApplyDocuments = job.autoApplyDocuments.filter(document => document.id !== documentId)
    dispatch(
      actions.updateJob({
        ...job,
        autoApplyDocuments,
      }),
    )
    apiClient
      .delete(`/job-cards/${job.id}/auto-apply-documents/${documentId}`)
      .then(() => {})
      // eslint-disable-next-line n/handle-callback-err
      .catch(err => {
        dispatch(
          actions.updateJob({
            ...job,
          }),
        )
        setUploadError({
          error: i18n.t('builder.job_search.delete_error'),
          documentType: docType,
          delete: true,
        })
      })
    resetFileInputSelection()
  }

  const containerRef = useRef(null)
  const handleDragLeave = e => {
    e.preventDefault()
    const container = containerRef.current
    if (!container.contains(e.relatedTarget)) {
      setIsDragging(false)
    }
  }

  const handleStatusChangeClick = () => {
    if (isPhone) {
      dispatch(actions.setOpenStatusChangeDrawer(true))
    } else {
      setStatusListOpen(!statusListOpen)
    }
  }

  const handleDrop = (event, docType) => {
    event.preventDefault()
    const droppedFiles = event.dataTransfer.files
    if (droppedFiles.length) {
      handleSubmit(droppedFiles[0], docType)
    }
    setIsDragging(false)
  }

  const applyButtons = isUserOnAutoApplyPlan ? (
    <>
      <Tooltip
        width={isPhone ? 190 : 250}
        multiline={true}
        style={{ width: '50%', zIndex: 99999 }}
        value={
          job.isEasyApply
            ? i18n.t('builder.job_search.auto_apply_disabled')
            : i18n.t('builder.job_search.auto_apply_description')
        }
      >
        <Button
          isDisabled={job.isEasyApply}
          style={{ width: '100%' }}
          onClick={handleAutoApplyClick}
        >
          <Icon24.Simple style={{ marginRight: '4px', width: '18px' }} />
          {i18n.t('builder.job_search.auto_apply.action_buttons.auto_apply')}
        </Button>
      </Tooltip>
      <Button style={{ width: '50%' }} onClick={handleApplyClick} theme={ButtonTheme.secondary}>
        <ApplyButtonIcon />
        {i18n.t('builder.job_search.auto_apply.action_buttons.manual_apply')}
      </Button>
    </>
  ) : (
    <PrimaryButton onClick={handleApplyClick}>
      <ExternalLink />
      Apply
    </PrimaryButton>
  )

  const buttonActions = showApply && (
    <ButtonContainer fromDashboard={isDashboard}>{applyButtons}</ButtonContainer>
  )

  const isRecommendedJob = status === ColumnName.recommended
  return (
    <Container ref={ref} isDashboard={isDashboard}>
      <HeaderContainer>
        {job.isIrrelevant && (
          <IrrelevantBox>
            <IrrelevantBoxTitle>Is this job still relevant?</IrrelevantBoxTitle>
            <IrrelevantBoxSubtitle>
              The job hasn&apos;t been updated for quite some time. This job might be outdated and
              no longer relevant.
            </IrrelevantBoxSubtitle>
            <Actions>
              <IrrelevantButton
                onClick={() => {
                  setRelevant(true)
                  handleBlur()
                }}
              >
                <TickIcon /> Still Relevant
              </IrrelevantButton>

              <IrrelevantDeleteButton onClick={() => onDelete(id)}>Delete</IrrelevantDeleteButton>
            </Actions>
          </IrrelevantBox>
        )}
        <Header>
          <CompanyLogoContainer>
            <CompanyLogo size={48} alt={fields.company} src={fields.logo} />
          </CompanyLogoContainer>
          <CompanyNameContainer>
            <CompanyLabel>{i18n.t('builder.job_tracking.edit_modal.company_label')}</CompanyLabel>
            <CompanyName
              value={companyState || ''}
              name="company"
              data-testid="companyInput"
              onBlur={handleBlur}
              onChange={e => {
                setCompanyState(e.target.value)
                handleUpdate(e)
              }}
            />
          </CompanyNameContainer>
          {status !== ColumnName.recommended && !isPhone && (
            <HeaderAction>
              <IconButton onClick={() => onDelete(id)}>
                <DeleteIcon />
              </IconButton>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </HeaderAction>
          )}
          {isPhone && (
            <>
              <DrawerActionContainer>
                <Icon20.More onClick={() => dispatch(actions.setOpenStatusChangeDrawer(true))} />
              </DrawerActionContainer>
              <DrawerActionContainer>
                <HeaderClose onClick={onClose} />
              </DrawerActionContainer>
            </>
          )}
        </Header>
        {!isPhone && buttonActions}
      </HeaderContainer>
      <ScrollContainer>
        <PaddedContainer>
          <InformationContainer>
            <Title>
              <Icon24.Complitness100 />
              {i18n.t('builder.job_tracking.sidebar.fields.status')}
            </Title>
            <StatusInfoContainer>
              <StatusesContainer>
                <Status onClick={handleStatusChangeClick} status={status}>
                  {statusListOpen && (
                    <StatusSelect
                      columnNames={columnNamesArray.filter(name => name !== ColumnName.deleted)}
                      selectedJob={job}
                      editJob={onSubmit}
                      closeSelect={() => setStatusListOpen(false)}
                      drawer={false}
                    />
                  )}
                </Status>
                {isUserOnAutoApplyPlan && job.autoApplyStatus && (
                  <AutoApplyStatusLabel autoApplyStatus={job.autoApplyStatus} />
                )}
              </StatusesContainer>
              {isUserOnAutoApplyPlan && job.autoApplyStatus && <AutoApplyJobStatusInfo job={job} />}
            </StatusInfoContainer>
          </InformationContainer>
          <InformationContainer>
            <Title>
              <Icon24.JobMedium />
              <span>Job Title</span>
            </Title>
            <Information
              placeholder="Empty"
              name="title"
              data-testid="titleInput"
              onBlur={handleBlur}
              onChange={e => {
                setTitleState(e.target.value)
                handleUpdate(e)
              }}
              value={titleState || ''}
            />
          </InformationContainer>
          <InformationContainer>
            <Title>
              <Icon24.Location1Medium />
              Location
            </Title>
            <Information
              name="location"
              placeholder="Empty"
              data-testid="locationInput"
              value={locationState || ''}
              onBlur={handleBlur}
              onChange={e => {
                setLocationState(e.target.value)
                handleUpdate(e)
              }}
            />
          </InformationContainer>
          <InformationContainer>
            <Title>
              <Icon24.UrlMedium />
              URL
            </Title>
            <Information
              name="link"
              placeholder="Empty"
              data-testid="urlInput"
              value={linkState || ''}
              onBlur={handleBlur}
              onChange={e => {
                setLinkState(e.target.value)
                handleUpdate(e)
              }}
            />
          </InformationContainer>
          {resumeId && (
            <InformationContainer>
              <TailoredResumeContainer>
                {showResumeWasTailoredBanner && (
                  <TailoredResumeReadyBanner>
                    <ResumeTailoring nobg />
                    <span>{i18n.t(`builder.job_tracking.tailoring.ready`)}</span>
                  </TailoredResumeReadyBanner>
                )}
                <TailoredResumeInformation>
                  <Title>
                    <Icon24.DocumentMedium />
                    <span>Resume</span>
                  </Title>
                  <TailoredResumeData>
                    <TailoredResumeName>
                      {job.resumeName || i18n.t('builder.resume_editor.untitled')}
                    </TailoredResumeName>
                    <TailoredResumeInteractions>
                      <Icon24.EditAlt onClick={handleResumeLink} />
                      <Icon24.DownloadIcon onClick={handleDownloadDocument} />
                    </TailoredResumeInteractions>
                  </TailoredResumeData>
                </TailoredResumeInformation>
              </TailoredResumeContainer>
            </InformationContainer>
          )}
          {resumeId && (
            <InformationContainer>
              <Title>
                <Icon24.DocumentMedium />
                <ResumeMatch>Resume Match</ResumeMatch>
              </Title>
              <InformationDiv>
                <ResumePercentage poorResumeScore={poorResumeScore}>
                  {resumeScore}%
                </ResumePercentage>
                <ResumeMatchText>
                  {poorResumeScore
                    ? i18n.t(`builder.job_tracking.resume_match.poor`)
                    : i18n.t(`builder.job_tracking.resume_match.perfect`)}
                </ResumeMatchText>
              </InformationDiv>
            </InformationContainer>
          )}
        </PaddedContainer>
        {!isRecommendedJob && (
          <>
            <TextAreaContainer>
              <FieldLabel
                onClick={() => {
                  setIsNotesVisible(!isNotesVisible)
                }}
              >
                <Icon24.EditMedium />
                {i18n.t(`builder.job_tracking.edit_modal.sections.notes`)}
                <Chevron collapsed={+isNotesVisible} />
              </FieldLabel>
              {isNotesVisible && (
                <Field
                  multiline
                  Icon={<img src={EditIcon} />}
                  name="notes"
                  value={notesState || ''}
                  onChange={e => {
                    setNotesState(e.target.value)
                    handleUpdate(e)
                  }}
                  onBlur={handleUpdateNote}
                />
              )}
            </TextAreaContainer>
            <TextAreaContainer
              ref={containerRef}
              onDragEnter={() => {
                setIsDocumentsVisible(true)
                setIsDragging(true)
              }}
              onDragLeave={handleDragLeave}
            >
              <FieldLabel
                id={documentSectionId}
                onClick={() => {
                  setIsDocumentsVisible(!isDocumentsVisible)
                }}
              >
                <Icon24.AttachMedium />
                {i18n.t('builder.job_tracking.edit_modal.sections.documents')}
                <Chevron collapsed={+isDocumentsVisible} />
              </FieldLabel>
              {isDocumentsVisible && (
                <UploadedDocuments
                  job={job}
                  isDragging={isDragging}
                  uploadError={uploadError}
                  uploadingFile={uploadingFile}
                  autoApplyDocuments={autoApplyDocuments}
                  resumeRefInput={fileInputRefResume}
                  coverLetterRefInput={fileInputRefCoverLetter}
                  isUserOnAutoApplyPlan={isUserOnAutoApplyPlan}
                  handleDrop={handleDrop}
                  setUploadError={setUploadError}
                  handleUploadDocument={handleChange}
                  handleDeleteDocument={handleDeleteDocument}
                  handleUploadDocumentButton={handleUploadDocumentButton}
                />
              )}
            </TextAreaContainer>
          </>
        )}
        <TextAreaContainer>
          <FieldLabel
            onClick={() => {
              setIsDescriptionVisible(!isDescriptionVisible)
            }}
          >
            <Icon24.JobMedium />
            {i18n.t('builder.job_tracking.edit_modal.sections.job_description')}
            <Chevron collapsed={+isDescriptionVisible} />
          </FieldLabel>

          {isDescriptionVisible && (
            <Field
              multiline
              name="description"
              value={descriptionState || ''}
              onBlur={handleBlur}
              onChange={e => {
                setDescriptionState(e.target.value)
                handleUpdate(e)
              }}
            />
          )}
        </TextAreaContainer>
        <PaddedContainer>
          {!isRecommendedJob && (
            <>
              <InformationContainer>
                <Title>
                  <Icon24.MoneyMedium />
                  Salary
                </Title>
                <Information
                  name="salary"
                  placeholder="Empty"
                  data-testid="salaryInput"
                  value={salaryState || ''}
                  onBlur={handleBlur}
                  onChange={e => {
                    setSalaryState(e.target.value)
                    handleUpdate(e)
                  }}
                />
              </InformationContainer>
            </>
          )}
          {!isRecommendedJob && (
            <DeleteButtonContainer>
              <DeleteButton onClick={() => onDelete(id)}>
                <Icon24.Delete />
                {i18n.t('builder.job_tracking.edit_modal.delete_button')}
              </DeleteButton>
            </DeleteButtonContainer>
          )}
        </PaddedContainer>
        <JobSearchInfo job={job} handleSetInterviewJob={handleSetInterviewJob} />
      </ScrollContainer>
      {isPhone && buttonActions}
      <SelfTailorResumeModal />
    </Container>
  )
}

export default Sidebar

Sidebar.propTypes = {
  job: PropTypes.object,
  columnNames: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  handleSetInterviewJob: PropTypes.func.isRequired,
  isDashboard: PropTypes.bool,
  toggleChangeStatusDrawer: PropTypes.func,
  showResumeWasTailoredBanner: PropTypes.bool,
  stateChangeRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any,
    }),
  ]),
}
